import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import set from 'lodash/set'
import { useState } from 'react'
import {
  getRequirementId,
  isRequirementRef,
  toRequirementRef,
} from '../../../../../util/authenticationContextRequirement.util'
import { INPUT_POLICY_PATH, isAuthenticationContextInput } from '../../policy_feature/inputs'

const deriveInitialState = (policyBody) => {
  const autoModeInputs = []
  const requirementIds = {}

  Object.keys(INPUT_POLICY_PATH)
    .filter(isAuthenticationContextInput)
    .forEach((input) => {
      const values = get(policyBody, INPUT_POLICY_PATH[input]) || []
      const references = values.filter(isRequirementRef).map(getRequirementId)
      if (references.length) {
        autoModeInputs.push(input)
        requirementIds[input] = references
      }
    })

  return { autoModeInputs, requirementIds }
}

// Exposed so a caller can seed a field-visibility list with fields that already resolve via a
// stored requirement, without waiting a render for the hook itself to report it.
export const getAutoModeAuthenticationContextInputs = (policyBody) => deriveInitialState(policyBody).autoModeInputs

// Owns, for a CA template's authentication-context fields, whether each one is resolved
// automatically by name at deploy (AC2/AC3) or still manually prompted — and keeps the field's
// raw JSON value ("authContext:<name>" references) in sync with whatever is attached. A
// requirement is just a name, so there is no separate definition to own here.
export default function useAuthenticationContextFieldRequirements({ initialPolicyBody, setPolicyBody }) {
  const [{ autoModeInputs, requirementIds }, setState] = useState(() => deriveInitialState(initialPolicyBody))

  const isAutoMode = (input) => autoModeInputs.includes(input)
  const getAttachedRequirementIds = (input) => requirementIds[input] || []

  // Functional setState so a write builds on any other field's edit in the same synchronous pass
  // (several requirement hooks hydrate one copied policy) rather than a stale snapshot.
  const writeFieldValue = (input, values) => {
    setPolicyBody((previousPolicyBody) => {
      const updated = cloneDeep(previousPolicyBody) || {}
      set(updated, INPUT_POLICY_PATH[input], values)
      return updated
    })
  }

  const toggleAutoMode = (input, enabled) => {
    setState((current) => ({
      autoModeInputs: enabled
        ? [...current.autoModeInputs, input]
        : current.autoModeInputs.filter((key) => key !== input),
      requirementIds: enabled ? current.requirementIds : { ...current.requirementIds, [input]: [] },
    }))

    if (!enabled) {
      writeFieldValue(input, [])
    }
  }

  // Matches the MultiSelectPillList contract of handing back the whole new selection on every
  // change, add or remove alike.
  const setAttachedRequirementIds = (input, updatedIds) => {
    setState((current) => ({ ...current, requirementIds: { ...current.requirementIds, [input]: updatedIds } }))
    writeFieldValue(input, updatedIds.map(toRequirementRef))
  }

  // Re-derives auto mode and attachments from a policy body bound after mount (a copied policy).
  const hydrate = (nextPolicyBody) => {
    setState(deriveInitialState(nextPolicyBody))
  }

  return { isAutoMode, getAttachedRequirementIds, toggleAutoMode, setAttachedRequirementIds, hydrate }
}
